import { useRecordContext, useGetList, useGetOne } from "react-admin";
import { Typography, Stack, Box, Chip } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import { Claimant, Policy, PolicyData, ResourceName } from "@/types";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { PolicyEnrollmentStatus } from "@/resources/policies/components/PolicyEnrollmentStatus";

const safeUSD = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? formatUSD(n) : "—");

const formatDate = (d?: string) => formatLongDate(d);

interface CurrentPolicyCardProps {
  context?: "claimant" | "client";
}

/** Stat row for displaying a label/value pair */
const StatRow = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} color={highlight ? "primary.main" : "text.primary"}>
      {value}
    </Typography>
  </Stack>
);

/** Divider line between stat rows */
const StatDivider = () => <Box sx={{ borderBottom: 1, borderColor: "divider" }} />;

/** Empty state when no policy exists */
const EmptyState = ({ context }: { context: string }) => (
  <Stack alignItems="center" spacing={2} py={4}>
    <Typography variant="body2" color="text.secondary">
      No policy found for this {context}.
    </Typography>
    <Button component={RouterLink} to="/policies/create" size={UIComponentSize.Small} startIcon={<AddIcon />}>
      Create Policy
    </Button>
  </Stack>
);

export const CurrentPolicyCard = ({ context = "claimant" }: CurrentPolicyCardProps) => {
  const record = useRecordContext<Claimant>();
  const navigate = useNavigate();

  // Permissions
  const { canShow: canShowPolicies } = useResourcePermissions(ResourceName.POLICIES);

  // Fetch policies for client context
  const { data: clientPolicies, isLoading } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: record?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: context === "client" && !!record?.id }
  );

  // Get policy based on context
  const policies = context === "claimant" ? record?.policies || [] : clientPolicies || [];

  const policy = policies[0]; // First policy (most recent)
  const policyId = policy?.id;

  // Get policy data for claimant context
  const policyData: PolicyData | undefined =
    context === "claimant" && policyId ? record?.policy_data?.[policyId] : undefined;

  // Fetch carrier info
  const { data: carrier } = useGetOne(
    ResourceName.VENDORS,
    { id: policy?.vendor_id_ref },
    { enabled: !!policy?.vendor_id_ref }
  );

  // Loading state
  if (context === "client" && isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading policies...
      </Typography>
    );
  }

  // Empty state
  if (!record || !policy) {
    return <EmptyState context={context} />;
  }

  // Policy values
  const specificDeductible = policy?.specific_benefit?.specific_deductible;
  const aggregateDeductible = policy?.aggregate_benefit?.aggregate_deductible;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {carrier?.company_name || "No Carrier"}
          </Typography>
        </Box>
        <StatusChip label={formatToTitleCase(policy.status)} color={getStatusColor(policy.status)} />
      </Stack>

      {/* Effective Dates */}
      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: 1,
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Policy Period
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {formatDate(policy.effective_start_date)} – {formatDate(policy.effective_end_date)}
        </Typography>
      </Box>

      {/* Enrollment status (US 5045 / ADR-047): "due" reminder, latest recorded
          month + trend, or a "nothing recorded yet" hint. */}
      <PolicyEnrollmentStatus policy={policy} onClick={() => navigate(`/policies/${policy.id}/show`)} />

      {/* Deductibles */}
      <StatRow label="Specific Deductible" value={safeUSD(specificDeductible)} />
      <StatDivider />
      <StatRow label="Aggregate Deductible" value={safeUSD(aggregateDeductible)} />

      {/* Lasered indicator for claimant */}
      {context === "claimant" && policyData?.is_lasered && (
        <Box sx={{ mt: 2 }}>
          <Chip
            label={`Lasered: ${safeUSD(policyData.lasered_attributes?.amt_lasered_deductible)}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}

      {/* Contract laser / full exclusion indicator (Feature #5287) */}
      {context === "claimant" && policyData?.is_contract_lasered && (
        <Box sx={{ mt: 2 }}>
          <Chip
            label={`Contract: ${safeUSD(policyData.amt_contract_lasered_deductible)}`}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}
      {context === "claimant" && policyData?.is_contract_excluded && (
        <Box sx={{ mt: 2 }}>
          <Chip label="Excluded" size="small" color="error" variant="outlined" sx={{ fontWeight: 500 }} />
        </Box>
      )}

      {/* View Policy Button */}
      {canShowPolicies && (
        <Button
          variant={ButtonStyleType.Outlined}
          size={UIComponentSize.Small}
          onClick={() => navigate(`/policies/${policy.id}/show`)}
          endIcon={<ArrowRightAltIcon sx={{ fontSize: 16 }} />}
          fullWidth
          sx={{ mt: 3 }}
        >
          View Policy Details
        </Button>
      )}
    </Box>
  );
};
